import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Paper,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  urlList: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  urlItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  urlChip: {
    maxWidth: '100%',
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
  metadataSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
}));

const UrlProcessorDialog = ({ open, onClose, onSubmit, onSuccess }) => {
  const classes = useStyles();
  const [urls, setUrls] = useState([]);
  const [urlInput, setUrlInput] = useState('');
  const [metadata, setMetadata] = useState({
    category: '',
    tags: [],
    public: true,
  });
  const [tagInput, setTagInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleAddUrl = () => {
    if (urlInput.trim() && isValidUrl(urlInput)) {
      setUrls([...urls, urlInput.trim()]);
      setUrlInput('');
      setError(null);
      setSuccess(null);
    } else {
      setError('Please enter a valid URL');
    }
  };

  const handleRemoveUrl = (urlToRemove) => {
    setUrls(urls.filter((url) => url !== urlToRemove));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      setMetadata({
        ...metadata,
        tags: [...new Set([...metadata.tags, tagInput.trim()])],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setMetadata({
      ...metadata,
      tags: metadata.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const handleSubmit = async () => {
    if (urls.length === 0) {
      setError('Please add at least one URL');
      return;
    }

    setProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await onSubmit(urls, metadata);
      const response = result?.data?.process_vector_document_urls;

      if (response?.success) {
        // Reset form on success
        setUrls([]);
        setMetadata({ category: '', tags: [], public: true });
        setProcessing(false);

        // Call onSuccess callback to show snackbar and close dialog
        if (onSuccess) {
          onSuccess(response.message);
        }
      } else {
        setError('Failed to queue URLs for processing');
        setProcessing(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to process URLs');
      setProcessing(false);
    }
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Process PDF URLs</DialogTitle>
      <DialogContent>
        {error && (
          <Paper style={{ padding: '12px', backgroundColor: '#f44336', color: 'white', marginBottom: '16px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{error}</Typography>
              <IconButton size="small" onClick={() => setError(null)} style={{ color: 'white' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )}

        {success && (
          <Paper style={{ padding: '12px', backgroundColor: '#4caf50', color: 'white', marginBottom: '16px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{success}</Typography>
              <IconButton size="small" onClick={() => { setSuccess(null); onClose(); }} style={{ color: 'white' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        )}

        <Typography variant="body2" color="textSecondary" gutterBottom>
          Add PDF URLs to process and index them in the RAG system. The system will extract text,
          generate summaries, and create embeddings for each document.
        </Typography>

        <Box mt={2}>
          <TextField
            label="PDF URL"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddUrl()}
            fullWidth
            placeholder="https://example.com/document.pdf"
            helperText="Enter a PDF URL and press Enter or click Add"
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddUrl}
            className={classes.addButton}
            size="small"
          >
            Add URL
          </Button>
        </Box>

        {urls.length > 0 && (
          <Box className={classes.urlList}>
            <Typography variant="subtitle2" gutterBottom>
              URLs to Process ({urls.length})
            </Typography>
            {urls.map((url, index) => (
              <Box key={index} className={classes.urlItem}>
                <Chip
                  label={url}
                  onDelete={() => handleRemoveUrl(url)}
                  className={classes.urlChip}
                  size="small"
                />
              </Box>
            ))}
          </Box>
        )}

        <Box className={classes.metadataSection}>
          <Typography variant="subtitle2" gutterBottom>
            Document Metadata (Applied to all URLs)
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              value={metadata.category}
              onChange={(e) => setMetadata({ ...metadata, category: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="valuation">Valuation</MenuItem>
              <MenuItem value="compliance">Compliance</MenuItem>
              <MenuItem value="marketing">Marketing</MenuItem>
              <MenuItem value="succession">Succession Planning</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>

          <Box mt={2}>
            <TextField
              label="Add Tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleAddTag}
              fullWidth
              size="small"
              helperText="Press Enter to add tags"
            />
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
              {metadata.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={metadata.public}
                onChange={(e) => setMetadata({ ...metadata, public: e.target.checked })}
              />
            }
            label="Make documents public (accessible to all users)"
            style={{ marginTop: 8 }}
          />
        </Box>

        {processing && (
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Processing PDFs...
            </Typography>
            <LinearProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={processing || urls.length === 0}
        >
          Process {urls.length} {urls.length === 1 ? 'URL' : 'URLs'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UrlProcessorDialog;
